import { fmtBwMinutes, fmtBwPercent, roundBwDecimal, roundBwMinutes } from './bandwidthFormat';

/** Build row/column labels and a dense matrix from sparse (row, col, value) rows. */

export type HeatmapValueUnit = 'min' | 'percent';

export interface HeatmapMatrix {
  rowLabels: string[];
  columnLabels: string[];
  cells: number[][];
}

/** Each cell is that row's share of the column total, 0–100. */
export function convertHeatmapCellsToPercent(cells: number[][]): number[][] {
  if (cells.length === 0) return cells;
  const colCount = Math.max(0, ...cells.map((row) => row.length));
  const colTotals = Array.from({ length: colCount }, (_, ci) =>
    cells.reduce((sum, row) => sum + (row[ci] ?? 0), 0)
  );
  return cells.map((row) =>
    row.map((value, ci) => {
      const total = colTotals[ci] ?? 0;
      return total > 0 && value > 0 ? (value / total) * 100 : 0;
    })
  );
}

export function formatHeatmapCellValue(value: number, unit: HeatmapValueUnit): string {
  if (value <= 0) return '-';
  if (unit === 'percent') {
    return fmtBwPercent(value);
  }
  return `${fmtBwMinutes(value)} min`;
}

/** Round heatmap export cells: whole minutes or 2-decimal percent. */
export function exportHeatmapCellValue(value: number, unit: HeatmapValueUnit): number | '' {
  if (value <= 0) return '';
  return unit === 'percent' ? roundBwDecimal(value) : roundBwMinutes(value);
}

export function buildHeatmapMatrix(
  items: Array<{ rowKey: string; colKey: string; value: number }>
): HeatmapMatrix {
  const rowSet = new Set<string>();
  const colSet = new Set<string>();
  for (const r of items) {
    rowSet.add(r.rowKey);
    colSet.add(r.colKey);
  }
  const rowLabels = [...rowSet].sort((a, b) => a.localeCompare(b));
  const columnLabels = [...colSet].sort((a, b) => a.localeCompare(b));
  const ri = new Map(rowLabels.map((r, i) => [r, i]));
  const ci = new Map(columnLabels.map((c, i) => [c, i]));
  const cells = rowLabels.map(() => columnLabels.map(() => 0));
  for (const r of items) {
    const i = ri.get(r.rowKey);
    const j = ci.get(r.colKey);
    if (i !== undefined && j !== undefined) {
      cells[i][j] += r.value;
    }
  }
  return { rowLabels, columnLabels, cells };
}
